/**
 * UrlaubAntraegePage — Vacation request management (admin/HR view).
 *
 * KPI bar · Tab filter (Offen/Genehmigt/Alle) · Table with quick-actions
 * Approve / Reject / Cancel with confirmation · Detail modal
 *
 * @page /urlaub/antraege
 * @roles administrator, ops_manager, teamleiter
 * @since v2.197.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, X, ChevronDown, Clock, CheckCircle, XCircle, AlertTriangle, Calendar, Ban, Eye, RefreshCw, Plus, Search } from 'lucide-react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import SharedAbwesenheitenTabs from '../components/SharedAbwesenheitenTabs';

const api = (path, opts) => fetch(path, { credentials: 'include', ...opts }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const apiPost = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiPut = (path, body) => api(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const fmtDate = (s) => { if (!s) return '—'; const d = new Date(s); return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`; };

// #1785: Stundenweise Antraege (Arztbesuch/Behoerdengang) wurden pauschal als "1 Tag"
//   angezeigt — der Genehmiger konnte nicht erkennen, ob es um 1,5 h oder einen ganzen
//   Tag geht. Eine Formatierstelle fuer Tabelle und Detail-Modal.
const fmtDauer = (a) => {
  const std = parseFloat(a?.stunden);
  if (a?.typ_zeitraum === 'stundenweise' && Number.isFinite(std) && std > 0) {
    const h = std.toLocaleString('de-AT', { maximumFractionDigits: 2 });
    const von = String(a.von_zeit || '').slice(0, 5);
    const bis = String(a.bis_zeit || '').slice(0, 5);
    return von && bis ? `${h} h (${von}–${bis})` : `${h} h`;
  }
  return a?.arbeitstage || a?.tage || '—';
};

const STATUS_BADGE = {
  beantragt:     { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', Icon: Clock },
  genehmigt:     { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', Icon: CheckCircle },
  abgelehnt:     { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', Icon: XCircle },
  storniert:     { bg: 'bg-[var(--bg-inset,#F8F9FC)]', border: 'border-[var(--border-default,#E5E7EB)]', text: 'text-[var(--text-tertiary,#6B7280)]', Icon: Ban },
  angetreten:    { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', Icon: Calendar },
  abgeschlossen: { bg: 'bg-[var(--bg-inset,#F8F9FC)]', border: 'border-[var(--border-default,#E5E7EB)]', text: 'text-[var(--text-tertiary,#6B7280)]', Icon: CheckCircle },
};

// #1783: Tab, in dem ein Antrag mit diesem Status liegt — muss zur Filter-Logik in
// `filtered` passen, sonst zeigt der Deep-Link hinter dem Modal eine leere Liste.
const TAB_FOR_STATUS = {
  beantragt: 'offen',
  genehmigt: 'genehmigt', angetreten: 'genehmigt', abgeschlossen: 'genehmigt',
  abgelehnt: 'abgelehnt', storniert: 'abgelehnt',
};

// ─── KPI card ───
function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="flex-1 min-w-[140px] p-5 border border-[var(--border-card,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)]">
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={14} style={{ color: accent || 'var(--text-tertiary,#6B7280)' }} />}
        <span className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{label}</span>
      </div>
      <div className="text-[22px] font-bold text-[var(--text-primary,#1A1A2E)]">{value}</div>
    </div>
  );
}

// ─── Status badge ───
function StatusBadge({ status, t }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.beantragt;
  const IconComp = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
      <IconComp size={12} /> {t(`urlaub.status.${status}`) || status}
    </span>
  );
}

// ─── Tab bar ───
function TabBar({ tabs, active, onChange }) {
  return (
    <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card,#E5E7EB)] inline-flex gap-1 mb-4 w-fit">
      {tabs.map(tab => (
        <button key={tab.key} onClick={() => onChange(tab.key)}
          className={`px-4 py-[7px] rounded-[8px] text-[13px] font-medium transition-all ${
            active === tab.key
              ? 'bg-white shadow-sm text-[var(--text-primary,#1A1A2E)] font-semibold dark:bg-[var(--bg-card)]'
              : 'text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]'
          }`}>
          {tab.label}{tab.count != null ? ` (${tab.count})` : ''}
        </button>
      ))}
    </div>
  );
}

// ─── Detail Modal ───
function DetailModal({ antrag, open, onClose, onAction, t, isAdmin }) {
  const [grund, setGrund] = useState('');
  const [ablehnungsgrund, setAblehnungsgrund] = useState('');
  const [showAblehnung, setShowAblehnung] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  if (!open || !antrag) return null;

  const handleAction = async (action) => {
    if (action === 'ablehnen' && !showAblehnung) {
      setShowAblehnung(true);
      return;
    }
    if (action === 'ablehnen' && !ablehnungsgrund.trim()) return;
    setActionLoading(action);
    try {
      const actionGrund = action === 'ablehnen' ? ablehnungsgrund.trim() : grund;
      await onAction(antrag.id, action, actionGrund, antrag.source);
      setShowAblehnung(false);
      setAblehnungsgrund('');
      onClose();
    } catch { /* handled in parent */ }
    setActionLoading(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">{t('urlaub.antraege.detail')}</h3>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-y-2 text-sm px-6 py-5">
          <span className="text-[var(--text-tertiary)]">{t('nav.employees')}</span>
          <span className="font-medium text-[var(--text-primary)]">{antrag.ma_name}</span>
          <span className="text-[var(--text-tertiary)]">{t('urlaub.antrag.typ')}</span>
          <span className="text-[var(--text-primary)]">{t(`urlaub.typ.${antrag.typ}`) || antrag.typ}</span>
          <span className="text-[var(--text-tertiary)]">{t('urlaub.vonDatum')}</span>
          <span className="text-[var(--text-primary)]">{fmtDate(antrag.von_datum)}</span>
          <span className="text-[var(--text-tertiary)]">{t('urlaub.bisDatum')}</span>
          <span className="text-[var(--text-primary)]">{fmtDate(antrag.bis_datum)}</span>
          <span className="text-[var(--text-tertiary)]">{t('urlaub.tage')}</span>
          <span className="font-semibold text-[var(--text-primary)]">{fmtDauer(antrag)}</span>
          <span className="text-[var(--text-tertiary)]">Status</span>
          <span><StatusBadge status={antrag.status} t={t} /></span>
          {antrag.notiz_ma && <>
            <span className="text-[var(--text-tertiary)]">{t('urlaub.notizMa')}</span>
            <span className="text-[var(--text-primary)] italic">{antrag.notiz_ma}</span>
          </>}
          {antrag.notiz_hr && <>
            <span className="text-[var(--text-tertiary)]">{t('urlaub.notizHr')}</span>
            <span className="text-[var(--text-primary)] italic">{antrag.notiz_hr}</span>
          </>}
          {antrag.ablehnungsgrund && <>
            <span className="text-[var(--text-tertiary)]">{t('urlaub.ablehnungsgrund', 'Ablehnungsgrund')}</span>
            <span className="text-red-700 italic">{antrag.ablehnungsgrund}</span>
          </>}
          {antrag.entschieden_von_name && <>
            <span className="text-[var(--text-tertiary)]">{t('urlaub.antraege.entschiedenVon')}</span>
            <span className="text-[var(--text-primary)]">{antrag.entschieden_von_name}</span>
          </>}
          {antrag.entschieden_am && <>
            <span className="text-[var(--text-tertiary)]">{t('urlaub.antraege.entschiedenAm')}</span>
            <span className="text-[var(--text-primary)]">{fmtDate(antrag.entschieden_am)}</span>
          </>}
          <span className="text-[var(--text-tertiary)]">{t('urlaub.antraege.erstelltAm')}</span>
          <span className="text-[var(--text-primary)]">{fmtDate(antrag.erstellt_am)}</span>
          {/* #1878: Arztnachweis aus dem Portal — Datei nur für HR-Rollen (administrator/ops_manager), audit_log */}
          {antrag.source === 'urlaub_antrag' && antrag.typ === 'krankenstand' && <>
            <span className="text-[var(--text-tertiary)]">{t('urlaub.antrag.arztnachweis', 'Arztnachweis')}</span>
            <span className="text-[var(--text-primary)]" data-testid="antrag-arztnachweis">
              {antrag.arztnachweis_datei
                ? (isAdmin
                  ? <a href={`/api/mitarbeiter/${antrag.mitarbeiter_id}/arztnachweise/${antrag.id}?inline=1`} target="_blank" rel="noreferrer" className="text-[#338edc] hover:underline">
                      {t('krankenstand.arztnachweisOeffnen', 'Arztnachweis öffnen')} →
                    </a>
                  : t('krankenstand.arztnachweisVorhanden', 'Arztnachweis vorhanden'))
                : (antrag.arztnachweis_erledigt
                  ? t('krankenstand.quittiertBadge', 'erledigt')
                  : t('krankenstand.arztnachweisFehlt', 'Arztnachweis fehlt'))}
            </span>
          </>}
        </div>

        {/* Action area */}
        {isAdmin && antrag.status === 'beantragt' && !showAblehnung && (
          <div className="border-t border-[var(--border-default)] pt-4 mx-6">
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.notizHr')}</label>
            <textarea
              className="w-full mb-3 px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)] resize-none"
              rows={2} value={grund} onChange={e => setGrund(e.target.value)}
              placeholder={t('urlaub.antraege.grundPlaceholder')}
            />
            <div className="flex gap-2">
              <button onClick={() => handleAction('genehmigen')} disabled={!!actionLoading}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white disabled:opacity-50 transition-colors">
                {actionLoading === 'genehmigen' ? '...' : <><Check size={14} /> {t('urlaub.genehmigen')}</>}
              </button>
              <button onClick={() => handleAction('ablehnen')} disabled={!!actionLoading}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-[10px] bg-[#EF4444] hover:bg-[#DC2626] text-white disabled:opacity-50 transition-colors">
                {actionLoading === 'ablehnen' ? '...' : <><X size={14} /> {t('urlaub.ablehnen')}</>}
              </button>
            </div>
          </div>
        )}

        {/* Ablehnungsgrund form */}
        {isAdmin && antrag.status === 'beantragt' && showAblehnung && (
          <div className="border-t border-[var(--border-default)] pt-4 mx-6">
            <label className="block text-xs font-medium text-red-700 mb-1">{t('urlaub.ablehnungsgrund', 'Ablehnungsgrund')} *</label>
            <textarea
              className="w-full mb-1 px-3 py-2 border border-red-300 rounded-lg text-sm bg-[var(--bg-card)] resize-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
              rows={3} value={ablehnungsgrund} onChange={e => setAblehnungsgrund(e.target.value)}
              placeholder={t('urlaub.ablehnungsgrund.placeholder', 'Bitte Grund der Ablehnung angeben...')}
              autoFocus
            />
            {!ablehnungsgrund.trim() && (
              <p className="text-xs text-red-600 mb-2">{t('urlaub.ablehnungsgrund.pflicht', 'Ablehnungsgrund ist ein Pflichtfeld')}</p>
            )}
            <div className="flex gap-2">
              <button onClick={() => { setShowAblehnung(false); setAblehnungsgrund(''); }}
                className="flex-1 px-3 py-2 text-sm font-medium rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button onClick={() => handleAction('ablehnen')} disabled={!!actionLoading || !ablehnungsgrund.trim()}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-[10px] bg-[#EF4444] hover:bg-[#DC2626] text-white disabled:opacity-50 transition-colors">
                {actionLoading === 'ablehnen' ? '...' : <><X size={14} /> {t('urlaub.ablehnen')}</>}
              </button>
            </div>
          </div>
        )}

        {isAdmin && (antrag.status === 'beantragt' || antrag.status === 'genehmigt') && (
          <div className={`${antrag.status === 'beantragt' ? 'mt-2 mx-6' : 'border-t border-[var(--border-default)] pt-4 mx-6'}`}>
            <button onClick={() => handleAction('stornieren')} disabled={!!actionLoading}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-[10px] border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
              {actionLoading === 'stornieren' ? '...' : <><Ban size={14} /> {t('urlaub.stornieren')}</>}
            </button>
          </div>
        )}

        {/* Close */}
        <div className="flex justify-end px-6 py-4 border-t border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
            {t('common.close') || 'Schließen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Neu Antrag Modal ───
function NeuAntragModal({ onClose, onSaved, t, preselectedMaId, lang }) {
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [abwesenheitsarten, setAbwesenheitsarten] = useState([]);
  const [maId, setMaId] = useState(preselectedMaId || '');
  const [typ, setTyp] = useState('urlaub');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [notiz, setNotiz] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [konto, setKonto] = useState(null);
  const [arbeitstage, setArbeitstage] = useState(null);
  // #927: stundenweise Erfassung (z.B. Arztbesuch)
  const [ganztaegig, setGanztaegig] = useState(true);
  const [vonZeit, setVonZeit] = useState('');
  const [bisZeit, setBisZeit] = useState('');

  useEffect(() => {
    Promise.all([
      api('/api/mitarbeiter?aktiv=true'),
      api('/api/abwesenheitsarten'),
    ]).then(([r, arten]) => {
      const list = (r.data || r || []).sort((a, b) => ((a.nachname || '') + (a.vorname || '')).localeCompare((b.nachname || '') + (b.vorname || '')));
      setMitarbeiter(list);
      setAbwesenheitsarten((arten.data || []).filter(a => a.aktiv));
    }).catch(() => {});
  }, []);

  // Load konto when MA or von-date selected (projected saldo to start date, v2.289.0)
  useEffect(() => {
    if (!maId) { setKonto(null); return; }
    const stichtagParam = von ? `?stichtag=${von}` : '';
    api(`/api/urlaub/konto/${maId}${stichtagParam}`).then(r => setKonto(r.aktuell || null)).catch(() => setKonto(null));
  }, [maId, von]);

  // Calculate work days (server-side, excludes weekends + holidays)
  useEffect(() => {
    if (!von || !bis || von > bis) { setArbeitstage(null); return; }
    const maParam = maId ? `&mitarbeiter_id=${maId}` : '';
    api(`/api/urlaub/berechne-arbeitstage?von=${von}&bis=${bis}${maParam}`)
      .then(r => setArbeitstage(r.tage ?? null))
      .catch(() => setArbeitstage(null));
  }, [von, bis, maId]);

  const verfuegbar = konto ? parseFloat(konto.saldo || 0) : null;

  // #1701: Gleitzeittag nur für Gleitzeit-MA anbieten (Backend-Guard gespiegelt)
  const selectedMa = mitarbeiter.find(m => String(m.id) === String(maId));
  const waehlbareArten = abwesenheitsarten.filter(a => a.key !== 'gleitzeittag' || selectedMa?.gleitzeit_aktiv);
  useEffect(() => {
    if (typ === 'gleitzeittag' && !selectedMa?.gleitzeit_aktiv) setTyp('urlaub');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maId]);

  // Determine quelle for selected typ
  const selectedArt = abwesenheitsarten.find(a => a.key === typ);
  const quelle = selectedArt?.quelle || 'urlaub_antraege';
  const brauchtSaldo = typ === 'urlaub';
  // #927: stundenweise nur möglich bei art.erlaubt_stundenweise (quelle='abwesenheiten')
  const erlaubtStundenweise = !!selectedArt?.erlaubt_stundenweise;
  const dauerH = (!ganztaegig && vonZeit && bisZeit) ? (() => {
    const [vh, vm] = vonZeit.split(':').map(Number);
    const [bh, bm] = bisZeit.split(':').map(Number);
    if ([vh, vm, bh, bm].some(n => Number.isNaN(n))) return null;
    const min = (bh * 60 + bm) - (vh * 60 + vm);
    return min > 0 ? Math.round((min / 60) * 100) / 100 : null;
  })() : null;

  // Beim Typ-Wechsel: stundenweise zurücksetzen wenn der neue Typ es nicht erlaubt
  useEffect(() => {
    if (!erlaubtStundenweise) { setGanztaegig(true); setVonZeit(''); setBisZeit(''); }
  }, [erlaubtStundenweise]);

  const handleSubmit = async () => {
    if (!maId || !von || !bis) return;
    if (!ganztaegig && erlaubtStundenweise && (!vonZeit || !bisZeit)) {
      setError(t('absence.time_required', 'Bitte von_zeit und bis_zeit angeben'));
      return;
    }
    if (!ganztaegig && erlaubtStundenweise && dauerH != null && dauerH <= 0) {
      setError(t('absence.time_invalid_range', 'bis_zeit muss nach von_zeit liegen'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // #1864 Folgeseite 3: stundenweiser Zeitausgleich lebt in `abwesenheiten` —
      //   urlaub_antraege kennt keine Uhrzeit, die Zeiten gingen dort still verloren.
      const stundenweiseFremd = quelle === 'urlaub_antraege' && erlaubtStundenweise && !ganztaegig && !!vonZeit && !!bisZeit;
      if (quelle === 'urlaub_antraege' && !stundenweiseFremd) {
        await apiPost('/api/urlaub/antraege', {
          mitarbeiter_id: parseInt(maId),
          von_datum: von,
          bis_datum: bis,
          typ,
          notiz_ma: notiz || undefined,
        });
      } else if (quelle === 'krankenstaende') {
        await apiPost('/api/urlaub/krankenstaende', {
          mitarbeiter_id: parseInt(maId),
          von_datum: von,
          bis_datum: bis,
          typ,
          notiz: notiz || undefined,
        });
      } else {
        // quelle === 'abwesenheiten' — oder stundenweiser ZA (stundenweiseFremd)
        const stundenweiseAktiv = erlaubtStundenweise && !ganztaegig && vonZeit && bisZeit;
        await apiPost('/api/abwesenheiten', {
          mitarbeiter_id: parseInt(maId),
          art_key: typ,
          von_datum: von,
          bis_datum: bis,
          von_zeit: stundenweiseAktiv ? vonZeit : undefined,
          bis_zeit: stundenweiseAktiv ? bisZeit : undefined,
          stunden: stundenweiseAktiv ? dauerH : undefined,
          notiz: notiz || undefined,
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.fehler?.map(f => f.text || f).join(', ') || err.error || 'Error');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-lg mx-4"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('abwesenheiten.neuerAntrag')}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.mitarbeiterWaehlen')} *</label>
            <select className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card)] text-[var(--text-primary)]"
              value={maId} onChange={e => setMaId(e.target.value)}>
              <option value="">—</option>
              {mitarbeiter.map(ma => (
                <option key={ma.id} value={ma.id}>{ma.nachname || ma.name} {ma.vorname || ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.typ', 'Typ')} *</label>
            <select className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card)] text-[var(--text-primary)]"
              value={typ} onChange={e => setTyp(e.target.value)}>
              {abwesenheitsarten.length > 0 ? (
                <>
                  {abwesenheitsarten.filter(a => a.quelle === 'urlaub_antraege').length > 0 && (
                    <optgroup label={t('urlaub.gruppe.urlaub', 'Urlaub')}>
                      {abwesenheitsarten.filter(a => a.quelle === 'urlaub_antraege').map(a => (
                        <option key={a.key} value={a.key}>{lang === 'de' ? a.label_de : (a.label_en || a.label_de)}</option>
                      ))}
                    </optgroup>
                  )}
                  {abwesenheitsarten.filter(a => a.quelle === 'krankenstaende').length > 0 && (
                    <optgroup label={t('urlaub.gruppe.krankenstand', 'Krankenstand')}>
                      {abwesenheitsarten.filter(a => a.quelle === 'krankenstaende').map(a => (
                        <option key={a.key} value={a.key}>{lang === 'de' ? a.label_de : (a.label_en || a.label_de)}</option>
                      ))}
                    </optgroup>
                  )}
                  {waehlbareArten.filter(a => a.quelle === 'abwesenheiten').length > 0 && (
                    <optgroup label={t('urlaub.gruppe.abwesenheit', 'Sonstige Abwesenheit')}>
                      {waehlbareArten.filter(a => a.quelle === 'abwesenheiten').map(a => (
                        <option key={a.key} value={a.key}>{lang === 'de' ? a.label_de : (a.label_en || a.label_de)}</option>
                      ))}
                    </optgroup>
                  )}
                </>
              ) : (
                <>
                  <option value="urlaub">{t('urlaub.typ.urlaub', 'Urlaub')}</option>
                  <option value="zeitausgleich">{t('urlaub.typ.zeitausgleich', 'Zeitausgleich')}</option>
                  <option value="sonderurlaub">{t('urlaub.typ.sonderurlaub', 'Sonderurlaub')}</option>
                </>
              )}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.von', 'Von')} *</label>
              <input type="date" className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card)]"
                value={von} onChange={e => { setVon(e.target.value); if (!bis || bis < e.target.value) setBis(e.target.value); }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.bis', 'Bis')} *</label>
              <input type="date" className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card)]"
                value={bis} onChange={e => setBis(e.target.value)} />
            </div>
          </div>

          {/* #927 Stundenweise Erfassung — nur bei Arten mit erlaubt_stundenweise */}
          {erlaubtStundenweise && (
            <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg p-3 bg-[var(--bg-inset,#F8F9FC)]">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input type="checkbox" checked={ganztaegig}
                  onChange={e => setGanztaegig(e.target.checked)}
                  className="accent-[#338edc] w-4 h-4" />
                <span className="text-sm font-medium text-[var(--text-primary)]">{t('absence.full_day', 'Ganztägig')}</span>
                <span className="text-xs text-[var(--text-tertiary)]">— {t('absence.full_day_hint', 'Zeit-Erfassung deaktiviert')}</span>
              </label>
              {!ganztaegig && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('absence.time_from', 'Von Uhrzeit')} *</label>
                    <input type="time" value={vonZeit} onChange={e => setVonZeit(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card)]" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('absence.time_to', 'Bis Uhrzeit')} *</label>
                    <input type="time" value={bisZeit} onChange={e => setBisZeit(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card)]" />
                  </div>
                </div>
              )}
              {!ganztaegig && dauerH != null && dauerH > 0 && (
                <p className="text-xs text-[var(--text-secondary)] mt-2">
                  {t('absence.duration_label', 'Dauer')}: <span className="font-semibold text-[var(--text-primary)]">{dauerH.toFixed(2).replace('.', ',')} h</span>
                  <span className="text-[var(--text-tertiary)] ml-2">— {t('absence.counts_as_present', 'zählt als Anwesenheit, bezahlt')}</span>
                </p>
              )}
            </div>
          )}
          {(arbeitstage != null || verfuegbar != null) && brauchtSaldo && (
            <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-sm space-y-1">
              {konto && (
                <>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">{t('urlaub.anspruch')}</span>
                    <span className="font-medium text-[var(--text-primary)]">{parseFloat(konto.anspruch || 0)} {t('urlaub.tage')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">{von ? t('urlaub.aufgebautBis', { datum: new Date(von + 'T00:00:00').toLocaleDateString('de-AT') }) : t('urlaub.aufgebaut')}</span>
                    <span className="font-medium text-[var(--text-primary)]">{parseFloat(konto.aufgebaut || 0).toFixed(2)} {t('urlaub.tage')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">− {t('urlaub.verbraucht')}</span>
                    <span className="font-medium text-[var(--text-primary)]">{parseFloat(konto.verbraucht || 0)} {t('urlaub.tage')}</span>
                  </div>
                  {parseFloat(konto.geplant || 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">− {t('urlaub.geplant')}</span>
                      <span className="font-medium text-[var(--text-primary)]">{parseFloat(konto.geplant || 0)} {t('urlaub.tage')}</span>
                    </div>
                  )}
                </>
              )}
              {arbeitstage != null && (
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">{t('urlaub.berechneteArbeitstage')}</span>
                  <span className="font-semibold text-[var(--text-primary)]">{arbeitstage}</span>
                </div>
              )}
              {verfuegbar != null && (
                <div className="flex justify-between border-t border-[var(--border-default)] pt-1 mt-1">
                  <span className="font-semibold text-[var(--text-primary)]">{von ? t('urlaub.verfuegbarAm', { datum: new Date(von + 'T00:00:00').toLocaleDateString('de-AT') }) : t('urlaub.verfuegbareTage')}</span>
                  <span className="font-bold text-[var(--text-primary)]">{Math.round(verfuegbar * 100) / 100}</span>
                </div>
              )}
              {arbeitstage != null && verfuegbar != null && arbeitstage > verfuegbar && (
                <p className="text-xs text-red-600 mt-1">{t('urlaub.nichtGenugTage')}</p>
              )}
              {arbeitstage != null && verfuegbar != null && arbeitstage <= verfuegbar && von && (
                <p className="text-xs text-emerald-600 mt-1">{t('urlaub.prognoseOk')}</p>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.notizMa', 'Notiz')}</label>
            <textarea rows={2} className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card)] resize-none"
              value={notiz} onChange={e => setNotiz(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
          <button onClick={onClose}
            className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button onClick={handleSubmit} disabled={saving || !maId || !von || !bis}
            className="px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
            {saving ? '...' : t('urlaub.antragEinreichen')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function UrlaubAntraegePage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isAdmin = ['administrator', 'admin', 'ops_manager'].includes(user?.rolle);

  const [loading, setLoading] = useState(true);
  const [antraege, setAntraege] = useState([]);
  const [tab, setTab] = useTabParam('offen');
  const [sortCol, setSortCol] = useState('erstellt_am');
  const [sortDir, setSortDir] = useState('desc');
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [showNewAntrag, setShowNewAntrag] = useState(false);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  // ─── Load ───
  const load = useCallback(() => {
    setLoading(true);
    // #1570: include_abwesenheiten — genehmigungspflichtige Portal-Abwesenheiten (Sonderurlaub etc.) mitladen
    api('/api/urlaub/antraege?include_abwesenheiten=1')
      .then(d => setAntraege(d.data || []))
      .catch(() => setAntraege([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ─── Deep-Link aus der Antrags-Benachrichtigung (#1783): ?antrag=<id>&src=<quelle> ───
  // Oeffnet den konkreten Antrag direkt. Laeuft genau einmal, danach werden die Parameter
  // entfernt, damit ein spaeteres Schliessen/Neuladen das Modal nicht erneut aufreisst.
  useEffect(() => {
    if (deepLinkHandled || loading) return;
    const id = searchParams.get('antrag');
    if (!id) return;
    const src = searchParams.get('src');
    setDeepLinkHandled(true);
    // #1499: die lokale ID ist erst zusammen mit der Quelle eindeutig — urlaub_antraege
    // und abwesenheiten haben getrennte ID-Raeume, die numerisch kollidieren.
    const hit = antraege.find(a => String(a.id) === id && (!src || a.source === src));
    if (hit) setDetail(hit);
    // Tab-Wechsel und Aufraeumen in EINEM Navigations-Update: zwei aufeinanderfolgende
    // setSearchParams-Aufrufe gehen beide vom selben, noch nicht geflushten Stand aus —
    // der zweite wuerde den gerade gesetzten Tab wieder verwerfen.
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('antrag');
      p.delete('src');
      if (hit) {
        const zielTab = TAB_FOR_STATUS[hit.status] || 'offen';
        if (zielTab === 'offen') p.delete('tab'); else p.set('tab', zielTab);
      }
      return p;
    }, { replace: true });
  }, [antraege, loading, deepLinkHandled, searchParams, setSearchParams]);

  // ─── KPI counts ───
  const counts = useMemo(() => {
    const c = { offen: 0, genehmigt: 0, abgelehnt: 0, storniert: 0 };
    antraege.forEach(a => {
      if (a.status === 'beantragt') c.offen++;
      else if (a.status === 'genehmigt' || a.status === 'angetreten' || a.status === 'abgeschlossen') c.genehmigt++;
      else if (a.status === 'abgelehnt') c.abgelehnt++;
      else if (a.status === 'storniert') c.storniert++;
    });
    return c;
  }, [antraege]);

  // ─── Filtered + sorted ───
  const filtered = useMemo(() => {
    let list = antraege;
    if (tab === 'offen') list = list.filter(a => a.status === 'beantragt');
    else if (tab === 'genehmigt') list = list.filter(a => ['genehmigt', 'angetreten', 'abgeschlossen'].includes(a.status));
    else if (tab === 'abgelehnt') list = list.filter(a => a.status === 'abgelehnt' || a.status === 'storniert');
    // search filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(a => (a.ma_name || '').toLowerCase().includes(q));
    }
    // sort
    list = [...list].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (sortCol === 'erstellt_am' || sortCol === 'von_datum') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [antraege, tab, sortCol, sortDir, search]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  // ─── Actions ───
  const handleAction = async (id, action, grund, source) => {
    setError(null);
    try {
      // #1570: Portal-Abwesenheiten (Sonderurlaub etc.) haben eigene Endpoints + eigene IDs.
      if (source === 'abwesenheit') {
        if (action === 'genehmigen') {
          await apiPut(`/api/abwesenheiten/${id}/genehmigen`, {});
        } else if (action === 'ablehnen') {
          await apiPut(`/api/abwesenheiten/${id}/ablehnen`, { grund: grund || 'Abgelehnt' });
        } else if (action === 'stornieren') {
          await apiPatch(`/api/abwesenheiten/${id}/stornieren`, { grund: grund || undefined });
        }
      } else if (action === 'genehmigen') {
        await apiPut(`/api/urlaub/antraege/${id}/genehmigen`, { notiz_hr: grund || undefined });
      } else if (action === 'ablehnen') {
        await apiPut(`/api/urlaub/antraege/${id}/ablehnen`, { grund: grund || 'Abgelehnt', ablehnungsgrund: grund || 'Abgelehnt', notiz_hr: grund || undefined });
      } else if (action === 'stornieren') {
        await apiPut(`/api/urlaub/antraege/${id}/stornieren`, { grund: grund || undefined });
      }
      load();
    } catch (err) {
      setError(err.error || err.message || 'Error');
      throw err;
    }
  };

  // Quick-action buttons (inline in table row)
  const QuickActions = ({ antrag }) => {
    const [busy, setBusy] = useState(null);
    if (antrag.status !== 'beantragt' || !isAdmin) return null;
    return (
      <div className="flex items-center gap-1">
        <button title={t('urlaub.genehmigen')} disabled={!!busy}
          onClick={async (e) => { e.stopPropagation(); setBusy('g'); try { await handleAction(antrag.id, 'genehmigen', undefined, antrag.source); } catch {} setBusy(null); }}
          className="p-1.5 rounded-lg text-[#338edc] hover:bg-blue-50 disabled:opacity-50">
          <Check size={15} />
        </button>
        <button title={t('urlaub.ablehnen')} disabled={!!busy}
          onClick={async (e) => { e.stopPropagation(); setBusy('a'); try { await handleAction(antrag.id, 'ablehnen', 'Abgelehnt', antrag.source); } catch {} setBusy(null); }}
          className="p-1.5 rounded-lg text-red-700 hover:bg-red-50 disabled:opacity-50">
          <X size={15} />
        </button>
      </div>
    );
  };

  const SortHeader = ({ col, children }) => (
    <th
      className="text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider px-3 py-2.5 cursor-pointer select-none hover:text-[var(--text-primary)]"
      onClick={() => toggleSort(col)}
    >
      <span className="inline-flex items-center gap-1">{children} {sortCol === col && <ChevronDown size={12} className={sortDir === 'asc' ? 'rotate-180' : ''} />}</span>
    </th>
  );

  return (
    <div className="p-6 max-w-full">
      <SharedAbwesenheitenTabs />
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-xl font-bold text-[var(--text-primary)]">{t('abwesenheiten.antraegeTitel')}</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              type="text"
              placeholder={t('urlaub.antraege.searchPlaceholder', 'Mitarbeiter suchen...')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs border border-[var(--border-default)] rounded-lg bg-[var(--bg-card)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] w-48"
            />
          </div>
          {isAdmin && (
            <button onClick={() => setShowNewAntrag(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white transition-colors">
              <Plus size={14} /> {t('abwesenheiten.neuerAntrag')}
            </button>
          )}
          <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">
            <RefreshCw size={14} /> {t('common.refresh') || 'Aktualisieren'}
          </button>
        </div>
      </div>

      {/* KPI bar */}
      <div className="flex gap-3 mb-5 flex-wrap">
        <Kpi label={t('urlaub.antraege.offen')} value={counts.offen} icon={Clock} accent="#EAB308" />
        <Kpi label={t('urlaub.antraege.genehmigt')} value={counts.genehmigt} icon={CheckCircle} accent="#22C55E" />
        <Kpi label={t('urlaub.antraege.abgelehnt')} value={counts.abgelehnt} icon={XCircle} accent="#EF4444" />
        <Kpi label={t('urlaub.antraege.storniert')} value={counts.storniert} icon={Ban} accent="#6B7280" />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <AlertTriangle size={14} /> {error}
          <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Tab filter */}
      <TabBar
        tabs={[
          { key: 'offen', label: t('urlaub.antraege.offen'), count: counts.offen },
          { key: 'genehmigt', label: t('urlaub.antraege.genehmigt'), count: counts.genehmigt },
          { key: 'abgelehnt', label: t('urlaub.antraege.abgelehntStorniert'), count: counts.abgelehnt + counts.storniert },
          { key: 'alle', label: t('urlaub.antraege.alle') },
        ]}
        active={tab}
        onChange={setTab}
      />

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-40 text-[var(--text-muted)]">
          <div className="w-5 h-5 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin mr-2" />
          {t('common.loading') || 'Laden...'}
        </div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[800px]">
              <thead>
                <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                  <SortHeader col="ma_name">{t('nav.employees')}</SortHeader>
                  <SortHeader col="typ">{t('urlaub.antrag.typ')}</SortHeader>
                  <SortHeader col="von_datum">{t('urlaub.antraege.zeitraum')}</SortHeader>
                  <th className="text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider px-3 py-2.5">{t('urlaub.tage')}</th>
                  <SortHeader col="status">Status</SortHeader>
                  <SortHeader col="erstellt_am">{t('urlaub.antraege.erstelltAm')}</SortHeader>
                  {isAdmin && <th className="text-right text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider px-3 py-2.5">{t('urlaub.antraege.aktionen')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                {filtered.map(a => (
                  <tr key={`${a.source||'u'}-${a.id}`} className="hover:bg-[var(--bg-inset,#F8F9FC)]/50 cursor-pointer" onClick={() => setDetail(a)}>
                    <td className="px-3 py-2.5 text-sm font-medium text-[var(--text-primary)] whitespace-nowrap">
                      {a.ma_name}
                      {a.kostenstelle && <span className="ml-1.5 text-[10px] text-[var(--text-muted)]">{a.kostenstelle}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-[var(--text-secondary)]">{t(`urlaub.typ.${a.typ}`) || a.typ}</td>
                    <td className="px-3 py-2.5 text-sm text-[var(--text-primary)] whitespace-nowrap">{fmtDate(a.von_datum)} – {fmtDate(a.bis_datum)}</td>
                    <td className="px-3 py-2.5 text-sm font-semibold text-[var(--text-primary)] whitespace-nowrap">{fmtDauer(a)}</td>
                    <td className="px-3 py-2.5"><StatusBadge status={a.status} t={t} /></td>
                    <td className="px-3 py-2.5 text-sm text-[var(--text-tertiary)] whitespace-nowrap">{fmtDate(a.erstellt_am)}</td>
                    {isAdmin && <td className="px-3 py-2.5 text-right"><QuickActions antrag={a} /></td>}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-sm text-[var(--text-muted)]">{t('common.noData') || 'Keine Anträge'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail modal */}
      <DetailModal
        antrag={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
        onAction={handleAction}
        t={t}
        isAdmin={isAdmin}
      />

      {/* Neuer Antrag modal (admin only) */}
      {showNewAntrag && <NeuAntragModal onClose={() => setShowNewAntrag(false)} onSaved={load} t={t} lang={language} />}
    </div>
  );
}
